import { useEffect, useState, useRef } from "react";
import Button from "../../components/Button";
import Table from "../../components/Table/Table";
import { TableSearch } from "../../components/Table/TableActions";
import Modal from "../../components/Modal";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import Badge from "../../components/Badge";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";
import SelectGroup from "../../components/FormComponent/SelectGroup";

const EMPLOYEE_ENDPOINT = "/api/v1/employees/";

const Teachers = ({ data }) => {
  const [searchResult, setSearchResult] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [batchReactivateModal, setBatchReactivateModal] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [teacherData, setTeacherData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBatchReactivating, setIsBatchReactivating] = useState(false);
  const selectRef = useRef(null);
  const [selectKey, setSelectKey] = useState(Date.now());

  const handleDeleteModal = (row) => {
    setCurrentRow(row);
    setDeleteModal(!deleteModal);
  };

  const handleBatchReactivateModal = () => {
    setBatchReactivateModal(!batchReactivateModal);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
  };

  const resetSelect = () => {
    setSelectKey(Date.now());
    if (selectRef.current) {
      selectRef.current.value = "";
    }
  };

  const handleBatchReactivate = () => {
    if (selectedRows.length === 0) {
      toast.error("Please select at least one employee to reactivate");

      return;
    }

    // Filter only inactive employees from selected rows
    const inactiveSelectedEmployees = selectedRows.filter(employeeId => {
      const employee = teacherData.find(emp => emp.employee_id === employeeId);

      return employee && !employee.is_active;
    });

    if (inactiveSelectedEmployees.length === 0) {
      toast.error("No inactive employees selected");

      return;
    }

    setIsBatchReactivating(true);

    const reactivateRequests = inactiveSelectedEmployees.map(employeeId => httprequest.patch(`${EMPLOYEE_ENDPOINT}${employeeId}/reactivate`)
    );

    Promise.all(reactivateRequests)
      .then((responses) => {
        setIsBatchReactivating(false);
        const successCount = responses.filter(response => response.status === 200).length;
        toast.success(`Successfully reactivated ${successCount} employee(s)`);
        setBatchReactivateModal(false);
        resetSelect();
        window.location.reload();
      })
      .catch(() => {
        setIsBatchReactivating(false);
        toast.error("Error reactivating some employees");
      });
  };

  const handleActionChange = (e) => {
    const action = e.target.value;

    if (action === "reactivate") {
      const hasInactiveSelected = selectedRows.some(employeeId => {
        const employee = teacherData.find(emp => emp.employee_id === employeeId);

        return employee && !employee.is_active;
      });

      if (!hasInactiveSelected) {
        toast.error("No inactive employees selected");
        resetSelect();

        return;
      }

      setBatchReactivateModal(true);
    }

    e.target.value = "";
  };

  const selectedInactiveCount = selectedRows.filter(employeeId => {
    const employee = teacherData.find(emp => emp.employee_id === employeeId);

    return employee && !employee.is_active;
  }).length;

  useEffect(() => {
    setTeacherData(data);
    setSearchResult(data);
  }, [data]);

  const columns = [
    {
      name: "Name",
      selector: (row) => `${row?.first_name} ${row?.last_name}`
    },
    {
      name: "Email Address",
      selector: row => row?.email
    },
    {
      name: "Role",
      selector: row => row?.role?.name
    },
    {
      name: "Active",
      cell: (row) => (<Badge status={row?.is_active} />)
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <Link to={`/users/employees/${row?.employee_id}`} className="table-action-icon">
            <Icon icon="fluent:eye-16-regular" />
          </Link>
          <Link to={`/users/employees/${row?.employee_id}`} className="table-action-icon">
            <Icon icon="heroicons:pencil-square-solid" />
          </Link>
          <button
            onClick={() => handleDeleteModal(row)}
            className="table-action-icon"
          >
            {row?.is_active === true ? <div className="delete-icon"><Icon icon="mingcute:delete-2-line" /></div>
              : <div className="approve-icon"><Icon icon="mdi:checkbox-marked-circle-outline" /></div>}
          </button>
        </div>
      )
    }
  ];

  // --| Filter Teacher table using name, email and class
  const handleSearchTeacher = (e) => {
    const { value } = e.target;

    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = teacherData?.filter((data) => valArray?.every(
      (word) => data?.first_name
        ?.toLowerCase()
        .includes(word.toLowerCase()) ||
        data?.last_name
          ?.toLowerCase()
          .includes(word.toLowerCase()) ||
        data?.email
          ?.toLowerCase()
          .includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  return (
    <>
      <Table
        data={searchResult}
        columns={columns}
        keyField="employee_id"
        handleRowSelected={({ selectedRows }) => setSelectedRows(selectedRows.map(row => row.employee_id))}
        subHeader={true}
        pagination
        subHeaderComponent={
          data?.length > 0 && (
            <div className="action_div">
              <TableSearch placeholder="Search here..." searchTableFunc={handleSearchTeacher} />

              {/* Batch Actions Dropdown */}
              {selectedRows.length > 0 && (
                <SelectGroup
                  children={
                    <select
                      name="batchActions"
                      ref={selectRef}
                      key={selectKey}
                      defaultValue=""
                      onChange={handleActionChange}
                      style={{ minWidth: "200px" }}
                    >
                      <option value="" disabled>Batch Actions ({selectedRows.length})</option>
                      {selectedInactiveCount > 0 && (
                        <option value="reactivate">Reactivate Selected ({selectedInactiveCount})</option>
                      )}
                    </select>
                  }
                />
              )}
            </div>
          )
        }
      />

      {/* Single Employee Action Modal */}
      <Modal
        title={currentRow?.is_active === true ? "Off board employee" : "Re-activate Employee"}
        subtitle={
          currentRow?.is_active === true
            ? (
              <div>
                Are you sure you want to off board
                <span className="modal-name-highlight"> {`${currentRow?.last_name} ${currentRow?.first_name}`}</span>?
                If deleted, the employee will no longer have access to this platform.
              </div>
            )
            : (
              <div>
                Are you sure you want to re-activate
                <span className="modal-name-highlight"> {`${currentRow?.last_name} ${currentRow?.first_name}`}</span>?
                If re-activated, the employee will have access to this platform.
              </div>
            )
        }
        modalContent={<></>}
        modalFooter={
          <>
            {currentRow?.is_active === true ? <Button variant="danger" buttonName="Off board Employee" buttonSize="full" color="btndefault" />
              : <Button variant="primary" buttonName="Re-activate Employee" buttonSize="full" color="btndefault" />}
          </>
        }
        isopen={deleteModal}
        closeModal={handleCloseDeleteModal}
      />

      {/* Batch Reactivate Modal */}
      <Modal
        title="Batch Reactivate Employees"
        subtitle={
          <div>
            Are you sure you want to reactivate <span className="modal-name-highlight">{selectedInactiveCount}</span> employee(s)?
            If re-activated, these employees will have access to this platform.
          </div>
        }
        modalContent={<></>}
        modalFooter={
          <>
            <Button
              variant="primary"
              isLoading={isBatchReactivating}
              onClick={handleBatchReactivate}
              buttonName={`Reactivate ${selectedInactiveCount} Employee(s)`}
              buttonSize="full"
              color="btndefault"
            />
          </>
        }
        isopen={batchReactivateModal}
        closeModal={handleBatchReactivateModal}
      />
    </>
  );
};

export default Teachers;
